import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import CheckIcon from '@material-ui/icons/Check';
import FacetOrderBoard from 'features/facets/components/FacetOrderBoard';
import {
  UnsavedChangesDialog,
  useUnsavedChangesGuard,
} from 'components/kea/UnsavedChangesGuard';
import { useFacetPolicyTranslation } from './facetPolicyI18n';

const DEFAULT_CAPABILITIES = {
  facet_policy_v1: true,
  facet_order_v1: true,
};

const PAGE_SIZE = 100;

function dataOf(envelope) {
  return envelope && envelope.data ? envelope.data : {};
}

function message(error, fallback) {
  return error && error.message ? error.message : fallback;
}

function mutationId(prefix = 'facet-order') {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function unique(items) {
  const seen = {};
  return (items || []).filter((item) => {
    const key = String(item.facet_key);
    if (seen[key]) return false;
    seen[key] = true;
    return true;
  });
}

function uniqueKeys(keys) {
  return Array.from(new Set((keys || []).map(String)));
}

function sameOrder(left, right) {
  return JSON.stringify({ head: left.head, tail: left.tail })
    === JSON.stringify({ head: right.head, tail: right.tail });
}

function mergeKnownOrder(previous, nextKnown, known) {
  const remaining = [...nextKnown];
  const merged = [];
  previous.forEach((key) => {
    if (!known.has(key)) {
      merged.push(key);
      return;
    }
    if (remaining.length) merged.push(remaining.shift());
  });
  return merged.concat(remaining);
}

function facetIssue(facet, t) {
  const warnings = Array.isArray(facet.warnings) ? facet.warnings : [];
  if (warnings.length) return String(warnings[0]);
  const orphaned = Number(facet.orphaned_value_count || 0);
  if (orphaned > 0) {
    return t('facets.policy.board.mappingIssues', '{count} mappings need attention', {
      count: orphaned.toLocaleString(),
    });
  }
  return null;
}

function toBoardFacet(facet, t) {
  const issue = facetIssue(facet, t);
  return {
    id: String(facet.facet_key),
    key: String(facet.facet_key),
    subtitle: facet.source === 'description.parameter'
      ? t('facets.policy.board.descriptionSource', 'From descriptions')
      : null,
    title: facet.title || facet.source_title || String(facet.facet_key),
    type: facet.type || 'string',
    values: Number.isFinite(Number(facet.distinct_value_count))
      ? Number(facet.distinct_value_count) : undefined,
    products: Number.isFinite(Number(facet.product_count))
      ? Number(facet.product_count) : undefined,
    health: issue ? 'attention' : 'ready',
    issue,
  };
}

export function buildFacetZones(catalogItems, configuredOrder, isVisible) {
  const items = Array.isArray(catalogItems) ? catalogItems : [];
  const keys = items.map(item => String(item.facet_key));
  const known = new Set(keys);
  const visible = new Set(keys.filter(isVisible));
  const first = uniqueKeys(configuredOrder.head)
    .filter(key => known.has(key) && visible.has(key));
  const firstSet = new Set(first);
  const last = uniqueKeys(configuredOrder.tail)
    .filter(key => known.has(key) && visible.has(key) && !firstSet.has(key));
  const explicit = new Set(first.concat(last));

  return {
    first,
    automatic: keys.filter(key => visible.has(key) && !explicit.has(key)),
    last,
    hidden: keys.filter(key => !visible.has(key)),
  };
}

export default function FacetPolicyList({
  api,
  capabilities,
  onOpenFacet,
  mutationIdFactory,
  refreshKey,
}) {
  const t = useFacetPolicyTranslation();
  const policyEnabled = capabilities.facet_policy_v1 === true;
  const orderEnabled = capabilities.facet_order_v1 === true;
  const catalogRequest = useRef(0);
  const orderMutation = useRef({ signature: null, id: null });
  const [catalog, setCatalog] = useState({
    items: [], loading: true, error: null, loaded: false,
  });
  const [order, setOrder] = useState({
    baseline: null,
    head: [],
    tail: [],
    loading: orderEnabled,
    saving: false,
    error: null,
    conflict: false,
  });
  const [visibilityBaseline, setVisibilityBaseline] = useState({});
  const [visibilityChanges, setVisibilityChanges] = useState({});
  const [saved, setSaved] = useState(false);

  const loadCatalog = useCallback(async () => {
    if (!policyEnabled || !api) return;
    const request = catalogRequest.current + 1;
    catalogRequest.current = request;
    setCatalog(previous => ({ ...previous, loading: true, error: null }));
    try {
      let cursor = null;
      let items = [];
      const cursors = new Set();
      do {
        const response = await api.listCatalog({
          query: '', type: null, issues_only: false, cursor, limit: PAGE_SIZE,
        });
        if (catalogRequest.current !== request) return;
        const data = dataOf(response);
        items = unique(items.concat(data.items || []));
        setCatalog({ items, loading: true, error: null, loaded: false });
        const next = data.next_cursor == null ? null : String(data.next_cursor);
        if (!next || cursors.has(next)) cursor = null;
        else {
          cursors.add(next);
          cursor = next;
        }
      } while (cursor);

      const baseline = items.reduce((result, facet) => {
        result[String(facet.facet_key)] = facet.visible !== false;
        return result;
      }, {});
      setVisibilityBaseline(baseline);
      setVisibilityChanges({});
      setCatalog({ items, loading: false, error: null, loaded: true });
    } catch (error) {
      if (catalogRequest.current !== request) return;
      setCatalog(previous => ({
        ...previous,
        loading: false,
        loaded: true,
        error: message(error, t('facets.policy.error.catalogLoad', 'Facet catalog failed to load.')),
      }));
    }
  }, [api, policyEnabled, t]);

  useEffect(() => {
    loadCatalog();
    return () => { catalogRequest.current += 1; };
  }, [loadCatalog, refreshKey]);

  useEffect(() => {
    let current = true;
    if (!orderEnabled || !api) {
      setOrder(previous => ({ ...previous, loading: false }));
      return undefined;
    }
    setOrder(previous => ({ ...previous, loading: true, error: null }));
    api.getOrder().then((response) => {
      if (!current) return;
      const data = dataOf(response);
      const baseline = {
        head: (data.head || []).map(String),
        tail: (data.tail || []).map(String),
        natural_count: Number(data.natural_count || 0),
        scope_revision: String(data.scope_revision),
      };
      setOrder(previous => ({
        ...previous,
        baseline,
        head: baseline.head,
        tail: baseline.tail,
        loading: false,
        conflict: false,
      }));
    }).catch((error) => {
      if (!current) return;
      setOrder(previous => ({
        ...previous,
        loading: false,
        error: message(error, t('facets.policy.error.orderLoad', 'Facet order failed to load.')),
      }));
    });
    return () => { current = false; };
  }, [api, orderEnabled, refreshKey, t]);

  const catalogKeys = useMemo(
    () => new Set(catalog.items.map(facet => String(facet.facet_key))),
    [catalog.items],
  );
  const effectiveVisibility = useCallback((key) => (
    Object.prototype.hasOwnProperty.call(visibilityChanges, key)
      ? visibilityChanges[key]
      : visibilityBaseline[key] !== false
  ), [visibilityBaseline, visibilityChanges]);

  const boardFacets = useMemo(() => catalog.items.reduce((result, facet) => {
    const mapped = toBoardFacet(facet, t);
    result[mapped.id] = mapped;
    return result;
  }, {}), [catalog.items, t]);

  const zones = useMemo(() => buildFacetZones(catalog.items, order, effectiveVisibility), [
    catalog.items,
    effectiveVisibility,
    order.head,
    order.tail,
  ]);

  const orderDirty = order.baseline ? !sameOrder(order, order.baseline) : false;
  const visibilityDirty = Object.keys(visibilityChanges).length > 0;
  const dirty = orderDirty || visibilityDirty;
  const unsavedGuard = useUnsavedChangesGuard(dirty);

  const applyZones = useCallback((next) => {
    if (orderEnabled) {
      setOrder(previous => ({
        ...previous,
        head: mergeKnownOrder(previous.head, next.first, catalogKeys),
        tail: mergeKnownOrder(previous.tail, next.last, catalogKeys),
        error: null,
        conflict: false,
      }));
    }
    const hidden = new Set(next.hidden);
    setVisibilityChanges(() => catalog.items.reduce((changes, facet) => {
      const key = String(facet.facet_key);
      const visible = !hidden.has(key);
      if (visible !== (visibilityBaseline[key] !== false)) changes[key] = visible;
      return changes;
    }, {}));
    setSaved(false);
  }, [catalog.items, catalogKeys, orderEnabled, visibilityBaseline]);

  const reset = useCallback(() => {
    if (order.baseline) {
      setOrder(previous => ({
        ...previous,
        head: previous.baseline.head,
        tail: previous.baseline.tail,
        error: null,
        conflict: false,
      }));
    }
    setVisibilityChanges({});
    setSaved(false);
  }, [order.baseline]);

  const save = useCallback(async () => {
    if (!dirty || order.saving) return;
    setOrder(previous => ({ ...previous, saving: true, error: null, conflict: false }));
    try {
      const visibilityEntries = Object.entries(visibilityChanges);
      const visibilityResponses = await Promise.all(visibilityEntries.map(async ([key, visible]) => {
        const facet = catalog.items.find(item => String(item.facet_key) === key);
        const response = await api.updatePolicy({
          facet_key: key,
          expected_revision: facet ? Number(facet.policy_revision ?? facet.revision ?? 0) : 0,
          client_mutation_id: mutationIdFactory('facet-visibility'),
          policy: { visible },
          assignment_changes: [],
          clear_assignment_ids: [],
          delete_group_ids: [],
          clear_all_assignments: false,
          validation_token: null,
        });
        return [key, visible, dataOf(response)];
      }));

      if (visibilityResponses.length) {
        setCatalog(previous => ({
          ...previous,
          items: previous.items.map((facet) => {
            const result = visibilityResponses.find(([key]) => key === String(facet.facet_key));
            if (!result) return facet;
            const [, visible, data] = result;
            return {
              ...facet,
              visible,
              policy_revision: data.policy_revision ?? facet.policy_revision,
              revision: data.policy_revision ?? facet.revision,
            };
          }),
        }));
        setVisibilityBaseline(previous => visibilityResponses.reduce((next, [key, visible]) => ({
          ...next,
          [key]: visible,
        }), previous));
        setVisibilityChanges({});
      }

      let nextBaseline = order.baseline;
      if (orderEnabled && orderDirty) {
        const signature = JSON.stringify({ head: order.head, tail: order.tail });
        if (orderMutation.current.signature !== signature) {
          orderMutation.current = { signature, id: mutationIdFactory('facet-order') };
        }
        const response = await api.updateOrder({
          expected_scope_revision: order.baseline.scope_revision,
          client_mutation_id: orderMutation.current.id,
          head: order.head,
          tail: order.tail,
        });
        const data = dataOf(response);
        nextBaseline = {
          head: (data.head || order.head).map(String),
          tail: (data.tail || order.tail).map(String),
          natural_count: Number(data.natural_count || order.baseline.natural_count || 0),
          scope_revision: String(data.scope_revision),
        };
        orderMutation.current = { signature: null, id: null };
      }

      setOrder(previous => ({
        ...previous,
        baseline: nextBaseline,
        head: nextBaseline ? nextBaseline.head : previous.head,
        tail: nextBaseline ? nextBaseline.tail : previous.tail,
        saving: false,
        conflict: false,
      }));
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2400);
    } catch (error) {
      setOrder(previous => ({
        ...previous,
        saving: false,
        conflict: error && error.code === 'REVISION_CONFLICT',
        error: message(error, t('facets.policy.error.orderSave', 'Filter changes failed to save.')),
      }));
      throw error;
    }
  }, [api, catalog.items, dirty, mutationIdFactory, order, orderDirty, orderEnabled, t, visibilityChanges]);

  if (!policyEnabled) {
    return <div className="fp-production-notice" role="status">{t('facets.policy.capabilityUnavailable', 'Facet policy capability unavailable.')}</div>;
  }

  if ((!catalog.loaded && catalog.loading) || order.loading) {
    return (
      <div className="fp-validation-empty is-loading" role="status" aria-live="polite">
        <span className="fp-spinner" />
        <h3>{t('facets.policy.loadingCatalog', 'Loading filters…')}</h3>
      </div>
    );
  }

  if (catalog.error && catalog.items.length === 0) {
    return <div className="fp-production-notice is-error" role="alert">{catalog.error}</div>;
  }

  return (
    <div data-testid="facet-policy-list">
      <FacetOrderBoard
        facets={boardFacets}
        zones={zones}
        onZonesChange={applyZones}
        onOpen={onOpenFacet || (() => {})}
        onReset={reset}
        onSave={save}
        dirty={dirty}
        translate={t}
      />
      {saved && <div className="fp-save-confirmation" role="status"><CheckIcon /> {t('facets.policy.order.saved', 'Filter changes saved')}</div>}
      {catalog.loading && <div className="fp-production-notice" role="status">{t('facets.policy.loadingCatalog', 'Loading filters…')}</div>}
      {catalog.error && <div className="fp-production-notice is-error" role="alert">{catalog.error}</div>}
      {order.error && <div className="fp-production-notice is-error" role="alert">{order.error}</div>}
      {order.conflict && <div className="fp-production-notice" role="status">{t('facets.policy.order.conflict', 'Order changed on the server. Your proposed order is still here; review it and save again.')}</div>}
      {!orderEnabled && <div className="fp-production-notice" role="status">{t('facets.policy.order.capabilityUnavailable', 'Filter ordering is unavailable. You can still edit individual filters.')}</div>}
      <UnsavedChangesDialog guard={unsavedGuard} />
    </div>
  );
}

FacetPolicyList.propTypes = {
  api: PropTypes.shape({
    listCatalog: PropTypes.func.isRequired,
    getOrder: PropTypes.func.isRequired,
    updateOrder: PropTypes.func.isRequired,
    updatePolicy: PropTypes.func.isRequired,
  }),
  capabilities: PropTypes.shape({
    facet_policy_v1: PropTypes.bool,
    facet_order_v1: PropTypes.bool,
  }),
  onOpenFacet: PropTypes.func,
  mutationIdFactory: PropTypes.func,
  refreshKey: PropTypes.number,
};

FacetPolicyList.defaultProps = {
  api: null,
  capabilities: DEFAULT_CAPABILITIES,
  onOpenFacet: null,
  mutationIdFactory: mutationId,
  refreshKey: 0,
};
